import React, { useState } from 'react';
import { Container, Table, Spinner, Alert, Card, Row, Col, Badge, Form, Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const TicketsScreen = () => {
  const queryClient = useQueryClient();
  const { data: { tickets = [], technicianOptions = [] } = {}, isLoading: loading, isError, error: queryError } = useQuery({
    queryKey: ['ticketsAndTechs'],
    queryFn: async () => {
      const [ticketsRes, techsRes] = await Promise.all([
        axios.get('/api/tickets'),
        axios.get('/api/tickets/technicians')
      ]);
      return { tickets: ticketsRes.data, technicianOptions: techsRes.data };
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true
  });
  const error = isError ? (queryError.response?.data?.message || queryError.message) : null;

  const [expandedTicketId, setExpandedTicketId] = useState(null);

  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [bulkPriority, setBulkPriority] = useState('');
  const [assignPriority, setAssignPriority] = useState({});
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);
  const [currentAttachments, setCurrentAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  const [departmentFilter, setDepartmentFilter] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [assignTech, setAssignTech] = useState({});
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentNoteTicketId, setCurrentNoteTicketId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showViewNotesModal, setShowViewNotesModal] = useState(false);
  const [ticketNotes, setTicketNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeTicketId, setCloseTicketId] = useState(null);

  const updateTicketInCache = (updater) => {
    queryClient.setQueryData(['ticketsAndTechs'], (old) => {
      if (!old) return old;
      return { ...old, tickets: updater(old.tickets) };
    });
  };

  const handleSelectTicket = (e, ticketId) => {
    e.stopPropagation();
    const newSelected = new Set(selectedTickets);
    if (newSelected.has(ticketId)) {
      newSelected.delete(ticketId);
    } else {
      newSelected.add(ticketId);
    }
    setSelectedTickets(newSelected);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedTickets(new Set(filteredTickets.map(t => t['Ticket ID'] || t.TicketID)));
    } else {
      setSelectedTickets(new Set());
    }
  };

  const handleBulkPriority = async () => {
    if (selectedTickets.size === 0 || !bulkPriority) return;
    setActionLoading(true);
    try {
      const res = await axios.post('/api/tickets/priority/bulk', { ticketIds: Array.from(selectedTickets), priority: bulkPriority });
      alert(res.data.message);
      setSelectedTickets(new Set());
      setBulkPriority('');
      // Invalidate or update cache here if needed, but a page refresh or waiting for the interval might suffice
    } catch (err) {
      alert('Failed to update priorities: ' + (err.response?.data?.message || err.message));
    }
    setActionLoading(false);
  };

  const handleUpdatePriority = async (ticketId) => {
    const prio = assignPriority[ticketId];
    if (!prio) return;
    setActionLoading(true);
    try {
      await axios.post(`/api/tickets/${ticketId}/priority`, { priority: prio });
      alert('Priority updated!');
      // Update local ticket priority
      updateTicketInCache(tks => tks.map(t => (t['Ticket ID'] === ticketId || t.TicketID === ticketId) ? { ...t, Priority: prio } : t));
    } catch (err) {
      alert('Failed to update priority: ' + (err.response?.data?.message || err.message));
    }
    setActionLoading(false);
  };

  const handleViewAttachments = async (ticketId) => {
    setAttachmentsLoading(true);
    setShowAttachmentsModal(true);
    try {
      const res = await axios.get(`/api/tickets/${ticketId}/attachments`);
      setCurrentAttachments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      alert('Failed to load attachments: ' + (err.response?.data?.message || err.message));
      setShowAttachmentsModal(false);
    }
    setAttachmentsLoading(false);
  };

  const handleAssign = async (ticketId) => {
    const tech = assignTech[ticketId];
    if (!tech) return;
    setActionLoading(true);
    try {
      await axios.post(`/api/tickets/${ticketId}/assign`, { technician: tech });
      
      const techFullName = technicianOptions.find(t => t.LoginId === tech)?.FullName || tech;
      updateTicketInCache(tks => tks.map(t => (t['Ticket ID'] === ticketId || t.TicketID === ticketId) ? { ...t, 'Assigned To Technician': techFullName } : t));
    } catch (err) {
      alert('Failed to assign ticket: ' + (err.response?.data?.message || err.message));
    }
    setActionLoading(false);
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !currentNoteTicketId) return;
    setActionLoading(true);
    try {
      await axios.post(`/api/tickets/${currentNoteTicketId}/note`, { noteText });
      setShowNoteModal(false);
      setNoteText('');
      alert('Note added successfully!');
      
      // Update local ticket note count
      updateTicketInCache(tks => tks.map(t => (t['Ticket ID'] === currentNoteTicketId || t.TicketID === currentNoteTicketId) ? { ...t, NoteCount: (t.NoteCount || 0) + 1 } : t));
    } catch (err) {
      alert('Failed to add note: ' + (err.response?.data?.message || err.message));
    }
    setActionLoading(false);
  };

  const handleCloseTicket = async () => {
    if (!noteText.trim() || !closeTicketId) return;
    setActionLoading(true);
    try {
      await axios.post(`/api/tickets/${closeTicketId}/close`, { noteText });
      setShowCloseModal(false);
      setNoteText('');
      alert('Ticket closed successfully!');
      
      // Remove ticket from list
      updateTicketInCache(tks => tks.filter(t => t['Ticket ID'] !== closeTicketId && t.TicketID !== closeTicketId));
    } catch (err) {
      alert('Failed to close ticket: ' + (err.response?.data?.message || err.message));
    }
    setActionLoading(false);
  };

  const handleViewNotes = async (ticketId) => {
    setNotesLoading(true);
    setShowViewNotesModal(true);
    try {
      const res = await axios.get(`/api/tickets/${ticketId}/notes`);
      setTicketNotes(res.data);
    } catch (err) {
      alert('Failed to load notes: ' + (err.response?.data?.message || err.message));
      setShowViewNotesModal(false);
    }
    setNotesLoading(false);
  };

  const toggleRow = (id) => {
    setExpandedTicketId(prev => (prev === id ? null : id));
  };



  const uniqueDepartments = [...new Set(tickets.map(t => t.Department || 'N/A'))].sort();
  const uniqueTechnicians = [...new Set(tickets.map(t => {
    return (!t['Assigned To Technician'] || t['Assigned To Technician'].includes('Blank <Enter Last Name>')) 
      ? 'Unassigned' : t['Assigned To Technician'];
  }))].sort();

  const filteredTickets = tickets.filter(t => {
    const dept = t.Department || 'N/A';
    const tech = (!t['Assigned To Technician'] || t['Assigned To Technician'].includes('Blank <Enter Last Name>')) 
      ? 'Unassigned' : t['Assigned To Technician'];
      
    const matchesDept = departmentFilter ? dept === departmentFilter : true;
    const matchesTech = assignedToFilter ? tech === assignedToFilter : true;
    
    return matchesDept && matchesTech;
  });

  const totalTickets = filteredTickets.length;
  const unassignedTickets = filteredTickets.filter(t => !t['Assigned To Technician'] || t['Assigned To Technician'].includes('Blank <Enter Last Name>')).length;
  const deptCounts = filteredTickets.reduce((acc, ticket) => {
    const dept = ticket.Department || 'Unassigned';
    acc[dept] = (acc[dept] || 0) + 1;
    return acc;
  }, {});

  return (
    <Container className="py-4">
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <Row className="mb-4">
            <Col md={4} className="mb-3 mb-md-0">
              <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '15px' }}>
                <Card.Body className="d-flex flex-column justify-content-center align-items-center p-4">
                  <h6 className="text-muted text-uppercase mb-3 fw-bold">Total Tickets</h6>
                  <h2 className="display-4 fw-bold text-primary m-0">{totalTickets}</h2>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-3 mb-md-0">
              <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '15px' }}>
                <Card.Body className="d-flex flex-column justify-content-center align-items-center p-4">
                  <h6 className="text-muted text-uppercase mb-3 fw-bold">Not Assigned</h6>
                  <h2 className="display-4 fw-bold text-danger m-0">{unassignedTickets}</h2>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '15px' }}>
                <Card.Body className="p-3">
                  <h6 className="text-muted text-uppercase mb-3 fw-bold text-center">By Department</h6>
                  <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                    <Table size="sm" borderless className="m-0">
                      <tbody>
                        {Object.entries(deptCounts).sort((a, b) => b[1] - a[1]).map(([dept, count], idx) => (
                          <tr key={idx}>
                            <td className="text-secondary">{dept}</td>
                            <td className="text-end"><Badge bg="secondary" pill>{count}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: '15px' }}>
            <Card.Body className="p-4">
              
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <div className="d-flex align-items-center gap-3">
                  <h2 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>Open Tickets</h2>
                  {selectedTickets.size > 0 && (
                    <div className="d-flex align-items-center gap-2 bg-light p-2 rounded border">
                      <span className="text-muted small fw-bold">{selectedTickets.size} selected</span>
                      <Form.Select size="sm" value={bulkPriority} onChange={(e) => setBulkPriority(e.target.value)} style={{ width: '130px' }}>
                        <option value="">Priority...</option>
                        {['1 - Critical', '2 - High', '3 - Medium', '4 - Low', '5 - Scheduled'].map(p => <option key={p} value={p}>{p}</option>)}
                      </Form.Select>
                      <Button size="sm" variant="primary" onClick={handleBulkPriority} disabled={actionLoading || !bulkPriority}>Apply</Button>
                    </div>
                  )}
                </div>

                <div className="d-flex gap-3 mt-3 mt-md-0">
                  <Form.Select 
                    value={departmentFilter} 
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    style={{ minWidth: '180px' }}
                  >
                    <option value="">All Departments</option>
                    {uniqueDepartments.map((dept, idx) => (
                      <option key={idx} value={dept}>{dept}</option>
                    ))}
                  </Form.Select>
                  <Form.Select 
                    value={assignedToFilter} 
                    onChange={(e) => setAssignedToFilter(e.target.value)}
                    style={{ minWidth: '180px' }}
                  >
                    <option value="">All Technicians</option>
                    {uniqueTechnicians.map((tech, idx) => (
                      <option key={idx} value={tech}>{tech}</option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th className="border-0" style={{ width: '40px' }}><Form.Check type="checkbox" onChange={handleSelectAll} checked={filteredTickets.length > 0 && selectedTickets.size === filteredTickets.length} /></th>
                      <th className="border-0">Summary</th>
                      <th className="border-0">Open Date</th>
                      <th className="border-0">Requestor</th>
                      <th className="border-0">Assigned To</th>
                      <th className="border-0">Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTickets.map((ticket, index) => {
                      const ticketId = ticket['Ticket ID'] || ticket.TicketID;
                      return (
                        <React.Fragment key={index}>
                          <tr 
                            onClick={() => toggleRow(ticketId)}
                            style={{ cursor: 'pointer' }}
                            data-ticket-id={ticketId} 
                            data-ticket-state={ticket.State}
                          >
                            <td>
                              <div onClick={(e) => e.stopPropagation()}>
                                <Form.Check type="checkbox" checked={selectedTickets.has(ticketId)} onChange={(e) => handleSelectTicket(e, ticketId)} />
                              </div>
                            </td>
                            <td><span className="fw-bold text-primary">{(ticket['Ticket Summary'] || ticket.TicketSummary || '').split(' - [')[0].trim()}</span></td>
                            <td className="text-muted">
                              {ticket['Open Date & Time'] ? new Date(ticket['Open Date & Time']).toLocaleString() : ''}
                            </td>
                            <td>{(ticket.Requestor || ticket['Requestor First Name'] || 'Unknown').replace('<Enter Last Name>', '').trim()}</td>
                            <td>{(!ticket['Assigned To Technician'] || ticket['Assigned To Technician'].includes('Blank <Enter Last Name>')) ? 'Unassigned' : ticket['Assigned To Technician']}</td>
                            <td>{ticket.Department || 'N/A'}</td>
                          </tr>
                          {expandedTicketId === ticketId && (
                            <tr className="bg-light">
                              <td colSpan="6" className="p-0 border-0">
                                <div className="p-4" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                                  <div className="d-flex flex-wrap align-items-center mb-4 p-3 bg-white border rounded shadow-sm w-100 gap-3">
                                    <div className="d-flex gap-2 align-items-center">
                                      <Form.Select 
                                        size="sm" 
                                        className="shadow-none border-secondary-subtle"
                                        style={{ width: '150px' }} 
                                        value={assignTech[ticketId] || ''} 
                                        onChange={(e) => setAssignTech({...assignTech, [ticketId]: e.target.value})}
                                      >
                                        <option value="">Technician...</option>
                                        {technicianOptions.map((tech, idx) => (
                                          <option key={idx} value={tech.LoginId}>{tech.FullName}</option>
                                        ))}
                                      </Form.Select>
                                      <Button size="sm" variant="primary" className="px-3 fw-semibold rounded-pill shadow-sm" onClick={() => handleAssign(ticketId)} disabled={actionLoading || !assignTech[ticketId]}>
                                        Assign
                                      </Button>
                                    </div>
                                    
                                    <div className="border-start" style={{ height: '30px', borderColor: '#dee2e6' }}></div>
                                    
                                    <div className="d-flex gap-2 align-items-center">
                                      <Form.Select 
                                        size="sm" 
                                        className="shadow-none border-secondary-subtle"
                                        style={{ width: '130px' }} 
                                        value={assignPriority[ticketId] || ticket.Priority || ''} 
                                        onChange={(e) => setAssignPriority({...assignPriority, [ticketId]: e.target.value})}
                                      >
                                        <option value="">Priority...</option>
                                        {['1 - Critical', '2 - High', '3 - Medium', '4 - Low', '5 - Scheduled'].map(p => <option key={p} value={p}>{p}</option>)}
                                      </Form.Select>
                                      <Button size="sm" variant="primary" className="px-3 fw-semibold rounded-pill shadow-sm" onClick={() => handleUpdatePriority(ticketId)} disabled={actionLoading || !assignPriority[ticketId]}>
                                        Update
                                      </Button>
                                    </div>

                                    <div className="border-start" style={{ height: '30px', borderColor: '#dee2e6' }}></div>
                                    
                                    <Button size="sm" variant="outline-primary" className="px-3 fw-semibold rounded-pill" onClick={() => { setCurrentNoteTicketId(ticketId); setNoteText(''); setShowNoteModal(true); }}>
                                      + Note
                                    </Button>
                                    <Button size="sm" variant="outline-secondary" className="px-3 fw-semibold rounded-pill" onClick={() => handleViewAttachments(ticketId)}>
                                      Attachments
                                    </Button>
                                    <Button size="sm" variant="outline-danger" className="px-3 fw-semibold rounded-pill" onClick={() => { setCloseTicketId(ticketId); setNoteText(''); setShowCloseModal(true); }}>
                                      Close Ticket
                                    </Button>

                                    {ticket.NoteCount > 0 && (
                                      <div className="ms-auto d-flex gap-2">
                                        <Button size="sm" variant="info" className="text-white d-flex align-items-center gap-2 shadow-sm fw-semibold px-3 rounded-pill" style={{ background: 'linear-gradient(45deg, #17a2b8, #117a8b)', border: 'none' }} onClick={() => handleViewNotes(ticketId)}>
                                          <span>Notes</span>
                                          <Badge bg="light" text="dark" pill className="shadow-sm">{ticket.NoteCount}</Badge>
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                  <h6 className="fw-bold text-secondary mb-2">Full Description</h6>
                                  
                                  <div 
                                    className="ticket-html-content"
                                    style={{ whiteSpace: 'pre-wrap', color: '#495057', lineHeight: '1.3' }}
                                    dangerouslySetInnerHTML={{ __html: (ticket.SubmittedText || ticket['Additional Information'] || 'No additional information provided.')
                                      .replace(new RegExp('</?pre>', 'gi'), '')
                                      .replace(/&nbsp;/gi, ' ')
                                      .replace(/<p>\s*<\/p>/gi, '')
                                      .replace(/<p><br\s*\/?>\s*<\/p>/gi, '')
                                      .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
                                      .replace(/<br\s*\/?>\s*\n/gi, '<br>')
                                      .replace(/<\/p>\s*\n/gi, '</p>')
                                    }}
                                  />

                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {filteredTickets.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No open tickets found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
      </>
      )}
      <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Note to Ticket {currentNoteTicketId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Note Text</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={5} 
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoteModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddNote} disabled={actionLoading || !noteText.trim()}>Update</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showViewNotesModal} onHide={() => setShowViewNotesModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Ticket Notes</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
          {notesLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : ticketNotes.length === 0 ? (
            <div className="text-center text-muted py-4">No notes found for this ticket.</div>
          ) : (
            ticketNotes.map((note, idx) => (
              <Card key={idx} className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center border-bottom-0 pt-3 pb-0">
                  <div className="fw-bold text-primary">{note.NoteType || 'Note'}</div>
                  <div className="text-muted small">
                    {note.Date ? new Date(note.Date).toLocaleString() : ''}
                  </div>
                </Card.Header>
                <Card.Body>
                  
                  <div 
                    className="ticket-html-content"
                    style={{ whiteSpace: 'pre-wrap', color: '#495057', lineHeight: '1.3' }}
                    dangerouslySetInnerHTML={{ __html: (note.Note || '')
                      .replace(new RegExp('</?pre>', 'gi'), '')
                      .replace(/&nbsp;/gi, ' ')
                      .replace(/<p>\s*<\/p>/gi, '')
                      .replace(/<p><br\s*\/?>\s*<\/p>/gi, '')
                      .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
                      .replace(/<br\s*\/?>\s*\n/gi, '<br>')
                      .replace(/<\/p>\s*\n/gi, '</p>')
                    }}
                  />

                </Card.Body>
                <Card.Footer className="bg-white border-top-0 pb-3 pt-0 text-muted small text-end">
                  Added by: <span className="fw-bold">{note.LastUser || 'Unknown'}</span>
                </Card.Footer>
              </Card>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewNotesModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Close Ticket {closeTicketId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Resolution Note</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={5} 
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter resolution details..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCloseModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleCloseTicket} disabled={actionLoading || !noteText.trim()}>Close Ticket</Button>
        </Modal.Footer>
      </Modal>
    
      <Modal show={showAttachmentsModal} onHide={() => setShowAttachmentsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Attachments</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {attachmentsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : currentAttachments.length === 0 ? (
            <div className="text-center text-muted py-4">No attachments found.</div>
          ) : (
            <Table size="sm" hover>
              <tbody>
                {currentAttachments.map((att, idx) => (
                  <tr key={idx}>
                    <td className="align-middle">
                      {att.OriginalFileName || att.FileName || 'Attachment'}
                    </td>
                    <td className="text-end">
                      <Button 
                        size="sm" 
                        variant="outline-primary"
                        href={`/api/tickets/attachment/${att.Sequence}`} 
                        target="_blank"
                      >
                        Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAttachmentsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>

  );
};

export default TicketsScreen;
